import { useState } from 'react';
import { functions } from 'fb';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserState } from 'hooks/User';
import { useNotifications } from 'hooks/Notifications';
import { getTimeSince } from 'utils';
import getNotificationIcon from './NotificationIcon';

// Material UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';

// Icons
import NotificationsIcon from '@mui/icons-material/NotificationsNoneRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import LoadIcon from '@mui/icons-material/CloudDownloadOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// Project components
import { PrimaryAvatar } from 'components/display/Avatars';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      content: '""',
    },
  },
}));

const Notifications = ({ light }: { light?: boolean }) => {
  const { t } = useTranslation(['common']);
  const [open, setOpen] = useState(false);
  const user = useUserState();
  const { notifications, moreToLoad, unread, loadMoreNotifications, markAsRead, newNotifications } = useNotifications();

  const read = (id: string) => {
    markAsRead(id);
    setOpen(false);
  };

  const openNotificationDialog = () => {
    setOpen(true);
    functions.httpsCallable('users-NoNewNotifications')();
  };

  const readAll = () => {
    notifications.forEach((notification) => {
      const data = notification.data();
      if (data && !data.read) {
        markAsRead(notification.id);
      }
    });
  };

  if (!user) {
    return null;
  } else {
    return (
      <>
        <IconButton onClick={openNotificationDialog}>
          <Badge badgeContent={newNotifications} color='primary'>
            <NotificationsIcon
              sx={(theme) => ({
                height: 26,
                width: 26,
                color: 'black',
                fill: theme.palette.black.main,
                [theme.breakpoints.down('lg')]: {
                  fill: light ? 'white' : theme.palette.text.primary,
                },
              })}
            />
          </Badge>
        </IconButton>
        <Dialog aria-labelledby='scroll-dialog-title' fullWidth maxWidth='md' onClose={() => setOpen(false)} open={open}>
          <DialogTitle id='scroll-dialog-title' sx={{ m: 0, p: 2 }}>
            <Typography sx={{ color: 'black.main' }} variant='h6'>
              {t('NOTIFICATIONS.HEADER')}
            </Typography>
            {notifications.find((n) => !n.data()?.read) && (
              <IconButton
                aria-label='close'
                onClick={() => readAll()}
                sx={(theme) => ({
                  position: 'absolute',
                  right: theme.spacing(7),
                  top: theme.spacing(1),
                  color: theme.palette.black.main,
                })}>
                <DoneAllIcon />
              </IconButton>
            )}
            <IconButton
              aria-label='close'
              onClick={() => setOpen(false)}
              sx={(theme) => ({
                position: 'absolute',
                right: theme.spacing(1),
                top: theme.spacing(1),
                color: theme.palette.black.main,
              })}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <List>
              {notifications.map((notification, index) => {
                const data = notification.data();
                if (!data) {
                  return null;
                }
                return (
                  <ListItemButton
                    component={Link}
                    divider={notifications.length - 1 !== index}
                    key={index}
                    onClick={() => read(notification.id)}
                    sx={data.read ? {} : { backgroundColor: 'background.paper' }}
                    to={data.click_action}>
                    <ListItemAvatar>
                      <StyledBadge
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        invisible={data.read}
                        overlap='circular'
                        variant='dot'>
                        <PrimaryAvatar>{getNotificationIcon(data.template)}</PrimaryAvatar>
                      </StyledBadge>
                    </ListItemAvatar>
                    <ListItemText primary={data.read ? data.title : <b>{data.title}</b>} secondary={data.body} />
                    <ListItemIcon>{data.read ? getTimeSince(data.time.toDate()) : <b>{getTimeSince(data.time.toDate())}</b>}</ListItemIcon>
                  </ListItemButton>
                );
              })}
            </List>
          </DialogContent>
          {moreToLoad && (
            <DialogActions>
              <Button color='secondary' endIcon={<LoadIcon />} fullWidth onClick={loadMoreNotifications} variant='outlined'>
                {t('NOTIFICATIONS.LOADMORE')}
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </>
    );
  }
};

export default Notifications;
